import React, { useState } from 'react';
import { LogIn, Fingerprint } from 'lucide-react';
import { api, getApiBase } from '../utils/api';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';
import Swal from 'sweetalert2';

// ==========================================
// ฟังก์ชันช่วยเหลือสำหรับ WebAuthn (แปลงข้อมูล)
// ==========================================
const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let charCode of bytes) {
    str += String.fromCharCode(charCode);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const Login = ({ setUser, setCurrentPage }) => {
  const [formData, setFormData] = useState({ national_id: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [tempAuthData, setTempAuthData] = useState(null);

  const proceedToDashboard = (userData) => {
    if (userData.is_super_admin) {
      setCurrentPage('super-admin-dashboard');
    } else if (userData.is_admin || userData.is_staff) {
      setCurrentPage('admin-dashboard');
    } else {
      setCurrentPage('face-scanner');
    }
  };

  const finishLogin = (authData) => {
    localStorage.setItem('auth_token', authData.token);
    localStorage.setItem('user_data', JSON.stringify(authData.user));
    
    setUser(authData.user);
    proceedToDashboard(authData.user);

    if (Capacitor.isNativePlatform()) {
      try {
        PushNotifications.requestPermissions().then(permStatus => {
          if (permStatus.receive === 'granted') {
            PushNotifications.register();
            PushNotifications.addListener('registration', async (token) => {
              await fetch(`${getApiBase()}update-fcm-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                  employee_id: authData.user.employee_id, 
                  fcm_token: token.value 
                })
              });
              console.log('✅ FCM Token updated');
            });
          }
        });
      } catch (pushError) {
        console.error('Push Notification Error:', pushError);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.national_id) {
        setError('กรุณาใส่เลขบัตรประชาชน');
        setLoading(false);
        return;
    }

    try {
      const data = await api.login(formData);

      if (data.success) {
        
        const hasPasskeyInDB = data.user.has_passkey; 
        const boundEmployeeId = localStorage.getItem('device_bound_to_employee');
        const hasSkipped = localStorage.getItem(`skip_faceid_${data.user.employee_id}`);
        
        if (!hasPasskeyInDB && !boundEmployeeId && !hasSkipped) {
          setTempAuthData(data);
          setShowPrompt(true);
        } else {
          finishLogin(data);
        }

      } else {
        setError(data.error || 'เข้าสู่ระบบไม่สำเร็จ');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterFaceID = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // 1. เช็คความพร้อมของอุปกรณ์แบบเจาะจง
        const check = await NativeBiometric.isAvailable();
        if (!check.isAvailable) {
          throw new Error('DEVICE_NOT_SECURE');
        }

        // 2. ดักจับกรณีผู้ใช้กดยกเลิก
        try {
          await NativeBiometric.verifyIdentity({
            reason: "ยืนยันตัวตนเพื่อลงทะเบียนอุปกรณ์นี้",
            title: "ลงทะเบียนอุปกรณ์",
          });
        } catch (verifyError) {
          throw new Error('USER_CANCELLED');
        }

        const credentialData = {
          employee_id: tempAuthData.user.employee_id,
          credential_id: 'native_device_' + tempAuthData.user.employee_id,
          public_key: 'native_auth_bypass', 
          client_data: 'capacitor_app'
        };
        await api.savePasskey(credentialData);

      } else {
        // 3. เช็คการรองรับ Passkey บนบราวเซอร์
        if (!window.PublicKeyCredential) {
          throw new Error('BROWSER_NOT_SUPPORTED');
        }

        const options = await api.getWebAuthnRegistrationOptions(tempAuthData.user.employee_id);
        options.challenge = base64urlToBuffer(options.challenge);
        options.user.id = base64urlToBuffer(options.user.id);

        const credential = await navigator.credentials.create({ publicKey: options });

        const credentialData = {
          employee_id: tempAuthData.user.employee_id,
          credential_id: credential.id,
          public_key: bufferToBase64url(credential.response.attestationObject), 
          client_data: bufferToBase64url(credential.response.clientDataJSON)
        };
        await api.savePasskey(credentialData);
      }

      localStorage.setItem('device_bound_to_employee', tempAuthData.user.employee_id);

      const updatedAuthData = {
        ...tempAuthData,
        user: { ...tempAuthData.user, has_passkey: true }
      };

      await Swal.fire({
        title: 'ลงทะเบียนอุปกรณ์สำเร็จ!',
        text: 'อุปกรณ์นี้ถูกผูกเข้ากับบัญชีของคุณแล้ว ไม่สามารถให้ผู้อื่นใช้ลงเวลาแทนได้',
        icon: 'success',
        confirmButtonText: 'เข้าสู่ระบบ',
        confirmButtonColor: '#3B82F6',
        customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl px-6 py-2 font-bold' }
      });
      
      setShowPrompt(false);
      finishLogin(updatedAuthData);
      
    } catch (error) {
      console.error('Registration failed:', error);
      
      // จัดการข้อความ Error เชิงรุก
      let errorMessage = 'คุณยกเลิกการสแกน หรืออุปกรณ์นี้ไม่รองรับ';
      if (error.message === 'DEVICE_NOT_SECURE') {
        errorMessage = 'อุปกรณ์ยังไม่ได้ตั้งค่าความปลอดภัย กรุณาไปตั้งค่ารหัสล็อคหน้าจอหรือลายนิ้วมือในเครื่องก่อน';
      } else if (error.message === 'USER_CANCELLED' || error.name === 'NotAllowedError') {
        errorMessage = 'คุณยกเลิกการทำรายการ';
      } else if (error.message === 'BROWSER_NOT_SUPPORTED') {
        errorMessage = 'อุปกรณ์หรือบราวเซอร์นี้ไม่รองรับระบบ Passkey';
      }

      Swal.fire({
        title: 'การลงทะเบียนถูกยกเลิก',
        text: errorMessage,
        icon: 'warning',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#6B7280',
        customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl px-6 py-2 font-bold' }
      });
    }
  };

  const handleSkip = () => {
    localStorage.setItem(`skip_faceid_${tempAuthData.user.employee_id}`, 'true');
    setShowPrompt(false);
    finishLogin(tempAuthData);
  };

  return (
    <div className="max-w-md mx-auto relative">
      {showPrompt && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl text-center animate-in zoom-in duration-300">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Fingerprint className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              ตั้งค่าสแกนประจำอุปกรณ์นี้
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              คุณสามารถผูก "อุปกรณ์เครื่องนี้" เพื่อใช้ Face ID ลงเวลาเข้างานได้ <br/>
              <span className="text-red-500 font-medium text-xs mt-2 block">(หมายเหตุ: 1 บัญชี สามารถผูกได้เพียง 1 อุปกรณ์เท่านั้น)</span>
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleRegisterFaceID}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-colors"
              >
                ผูกอุปกรณ์นี้ทันที
              </button>
              <button
                onClick={handleSkip}
                className="w-full bg-gray-100 text-gray-600 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                ข้ามไปก่อน
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-8 rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <LogIn className="h-12 w-12 text-blue-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">เข้าสู่ระบบ</h1>
          <p className="text-gray-600 mt-2">เพื่อดูรายงานและจัดการข้อมูล</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">เลขบัตรประชาชน</label>
            <input
              type="text"
              value={formData.national_id}
              onChange={(e) => setFormData({...formData, national_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="13 หลัก"
              maxLength="13"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
          >
            {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;